/**
 * What every page shares: the navigation, and a footer that states who the
 * merchant is. The footer is where the registered business details live for
 * compliance — legal name, registration number, registered address, contact,
 * governing law and the policy links — so they appear on every page rather than
 * only on the ones a reviewer happens to open.
 */

import { h } from '../lib/dom.js';
import { CONTENT, LANGS, LANG_LABEL } from '../content.js';
import { COMPANY } from '../company.js';
import { pathFor } from '../lib/router.js';

const wordmark = '/assets/veni-wordmark.svg';

/**
 * An in-app link: a real href for crawlers and new tabs, handled without a
 * reload. `page` is null for the home page, as in the router.
 */
export function routeLink(lang, page, text, attrs = {}) {
  return h('a', { href: pathFor(lang, page), dataset: { route: '' }, ...attrs }, text);
}

export function shell(route, content, { onLangChange }) {
  const { lang } = route;
  return h(
    'div',
    { class: 'page', dataset: { lang } },
    h('a', { class: 'skip-link', href: '#main' }, 'Skip to content'),
    nav(route, onLangChange),
    h('main', { class: 'shell', id: 'main' }, content, h('hr', { class: 'hr', style: 'margin: 56px 0 0' }), footer(lang)),
  );
}

function nav({ lang, page }, onLangChange) {
  const c = CONTENT[lang];
  // In-page anchors only mean something on the home page; elsewhere they lead back to it.
  const anchor = (id) => (page ? `${pathFor(lang, null)}#${id}` : `#${id}`);
  return h(
    'nav',
    { class: 'nav' },
    h(
      'span',
      { class: 'nav-brand' },
      routeLink(lang, null, h('img', { src: wordmark, alt: 'Veni', width: 120, height: 22 }), { 'aria-label': 'Veni — home' }),
    ),
    h('a', { class: 'nav-link', href: anchor('services') }, c.nav.services),
    h('a', { class: 'nav-link', href: anchor('about') }, c.nav.about),
    h('a', { class: 'nav-link', href: anchor('fees') }, c.nav.fees),
    h('a', { class: 'nav-link', href: anchor('contact') }, c.nav.contact),
    routeLink(lang, 'pay', c.nav.pay, { class: 'nav-link', 'aria-current': page === 'pay' ? 'page' : null }),
    h(
      'span',
      { class: 'lang-switch', role: 'group', 'aria-label': 'Language' },
      LANGS.map((l) =>
        h(
          'button',
          {
            type: 'button',
            class: 'lang-btn',
            lang: l,
            dataset: { lang: l },
            'aria-current': l === lang ? 'true' : 'false',
            onClick: () => onLangChange(l),
          },
          LANG_LABEL[l],
        ),
      ),
    ),
  );
}

function footer(lang) {
  const f = CONTENT[lang].footer;
  const br = COMPANY.brNumber || f.pending;
  return h(
    'footer',
    { class: 'footer' },
    h(
      'div',
      { class: 'footer-legal' },
      h(
        'p',
        { class: 'footer-entity' },
        h('strong', null, COMPANY.legalName),
        ' · ',
        `${f.br} ${br}`,
        h('br'),
        COMPANY.registeredAddress,
        h('br'),
        h('a', { href: `mailto:${COMPANY.email}` }, COMPANY.email),
        ' · ',
        h('a', { href: `tel:${COMPANY.phone.replace(/\s/g, '')}` }, COMPANY.phone),
      ),
      h(
        'p',
        { class: 'footer-links' },
        [
          routeLink(lang, 'terms', f.terms),
          routeLink(lang, 'privacy', f.privacy),
          routeLink(lang, 'refunds', f.refunds),
          routeLink(lang, 'pay', f.pay),
        ].flatMap((link, i) => (i ? [' · ', link] : [link])),
      ),
      h('p', { class: 'footer-law' }, f.law),
    ),
    h('div', { class: 'footer-base' }, h('span', null, f.company), h('span', { class: 'places' }, f.places)),
  );
}
